import React, { useState } from 'react';
import { BrowserRouter, Link, Route, Routes } from 'react-router-dom';
import styled from 'styled-components';
import chroma from 'chroma-js';
import { ChromePicker } from 'react-color';
import { useDropzone } from 'react-dropzone';
import ColorThief from 'colorthief';
import ColorPickerComponent from './components/ColorPicker';
import About from './components/About';
import Contact from './components/Contact';
import PrivacyPolicy from './components/PrivacyPolicy';
import logoImage from './assets/logo.png'; // Adjust path as necessary
import WhyColorsMatter from './components/WhyColorsMatter'; // Import the new component
import { PiMouseScrollBold } from "react-icons/pi";

const AppContainer = styled.div`
display: flex;
flex-direction: column;
align-items: center;
justify-content: space-between;
font-family: 'Pixelify Sans', sans-serif;
background-color: #f0f0f0;
min-height: 100vh;
padding: 80px 20px 20px; /* Adjust padding-top to navbar height */
box-sizing: border-box;
width: 90%; /* Ensure full width */
max-width: 1000px; /* Adjust max-width as needed */
margin: 0 auto; /* Center align and add spacing */
`;

const Header = styled.h1`
  font-size: 2rem;
  color: #24342c;
  margin-bottom: 20px;
  text-align: center;
  font-family: 'aria-label', sans-serif;
`;

const NavBar = styled.nav`
width: 100%;
  background-color: #4b6353;
  padding: 10px 0;
  text-align: center;
  font-family: 'aria-label', sans-serif;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000; /* Ensure it's above other elements */
`;

const NavLinkStyled = styled(Link)`
  margin: 0 15px;
  color: white;
  text-decoration: none;
  font-size: 1rem;

  &:hover {
    text-decoration: underline;
  }
`;

const InputContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;
  width: 100%;
`;

const Input = styled.input`
  padding: 12px;
  font-size: 1rem;
  border: 2px solid #ccc;
  border-radius: 4px;
  width: 100%;
  max-width: 300px;
  margin: 0 10px 10px 0;
`;

const Button = styled.button`
  padding: 12px 24px;
  font-size: 1rem;
  background-color: #1a71ab;
  color: #90d0ff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin: 0 10px 10px 0;
  transition: background-color 0.3s ease;
  font-family:'aria-label', sans-serif;;

  &:hover {
    background-color: #005b93;
  }
`;

const DropzoneContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 300px;
  padding: 20px;
  border: 2px dashed #007bff;
  border-radius: 4px;
  cursor: pointer;
  margin: 20px 0;
`;

const CustomChromePicker = styled(ChromePicker)`
  .chrome-picker {
    width: 300px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  }
`;

const GenerateButton = styled(Button)`
  margin-top: 20px;
`;

const ColorDisplay = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 30px;
`;

const ColorBoxContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 5px;
`;

const ColorBox = styled.div`
  width: 100px;
  height: 100px;
  background-color: ${(props) => props.color};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #000;
  font-weight: bold;
  position: relative;
`;

const ColorCode = styled.div`
  position: absolute;
  top: -20px;
  right: -20px;
  background-color: rgba(255, 255, 255, 0.8);
  padding: 5px;
  border-radius: 4px;
  font-size: 0.8rem;
`;

const SampleBox = styled.div`
  margin-top: 30px;
  padding: 20px;
  border-radius: 8px;
  width: 100%;
  max-width: 300px;
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 4.5rem;
  color: ${(props) => props.textColor};
  background-color: ${(props) => props.bgColor};
  border: 5px solid ${(props) => props.borderColor};
  text-align: center;
`;

const AdditionalColorsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 20px;
`;

const AdditionalColorBox = styled.div`
  width: 60px;
  height: 60px;
  background-color: ${(props) => props.color};
  border-radius: 50%;
  margin: 5px;
  cursor: pointer;
  transition: transform 0.2s ease;
  border: 2px solid transparent;

  &:hover {
    transform: scale(1.1);
  }

  &.selected {
    border-color: #007bff;
  }
`;

const ContentContainer = styled.div`
padding: 20px;
margin-bottom: 20px;
border: 1px solid #ccc;
border-radius: 8px;
background-color: #d8deda;
max-width: 800px;
text-align: left;
color: #404a44;
  font-family: 'aria-label', sans-serif;
`;

const Line = styled.hr`
  width: 100%;
  border: 1px solid #ccc;
  margin: 20px 0;
`;

const SubHeader = styled.h2`
font-size: 1.5rem;
color: #24342c;
margin-bottom: 20px;
text-align: center;
font-family: 'aria-label', sans-serif;
`;


const App = () => {
  const [inputColor, setInputColor] = useState('#3498db');
  const [colorPalette, setColorPalette] = useState([]);
  const [sampleTextColor, setSampleTextColor] = useState('');
  const [sampleBgColor, setSampleBgColor] = useState('');
  const [sampleBorderColor, setSampleBorderColor] = useState('');
  const [displayText, setDisplayText] = useState('Hello World');
  const [additionalColors, setAdditionalColors] = useState([]);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [selectedColorIndex, setSelectedColorIndex] = useState(null);

  const generateColorPalette = (color) => {
    const colors = chroma.scale([color, chroma(color).brighten(2)]).mode('lch').colors(3);
    return colors;
  };

  const generateAdditionalColors = (color) => {
    const additionalColors = chroma.scale(['white', color, 'black']).mode('lch').colors(6);
    return additionalColors.filter((c) => c !== color);
  };

  const handleColorChange = (color) => {
    setInputColor(color.hex);
  };

  const handleGeneratePalette = () => {
    try {
      const palette = generateColorPalette(inputColor);
      setColorPalette(palette);
      setSampleBgColor(palette[0]);
      setSampleBorderColor(palette[1]);
      setSampleTextColor(palette[2]);
      setAdditionalColors(generateAdditionalColors(inputColor));
    } catch (e) {
      alert('Invalid color format. Please enter a valid HEX or RGBA color.');
    }
  };

  const handleGenerateRandomColor = () => {
    const randomColor = chroma.random().hex();
    setInputColor(randomColor);
    const palette = generateColorPalette(randomColor);
    setColorPalette(palette);
    setSampleBgColor(palette[0]);
    setSampleBorderColor(palette[1]);
    setSampleTextColor(palette[2]);
    setAdditionalColors(generateAdditionalColors(randomColor));
  };

  const onDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const colorThief = new ColorThief();
        const dominantColor = colorThief.getColor(img);
        const palette = colorThief.getPalette(img, 3);
        setUploadedImage(event.target.result);
        const hexPalette = palette.map((color) => chroma(color).hex());
        setColorPalette(hexPalette);
        setSampleBgColor(hexPalette[0]);
        setSampleBorderColor(hexPalette[1]);
        setSampleTextColor(hexPalette[2]);
        setAdditionalColors(generateAdditionalColors(chroma(dominantColor).hex()));
      };
    };
    reader.readAsDataURL(file);
  };

  const { getRootProps, getInputProps } = useDropzone({ onDrop }); // Destructure getRootProps and getInputProps

  const handleColorBoxClick = (index, color) => {
    setInputColor(color);
    setSelectedColorIndex(index);
  };

  // Define Footer styled component
  const Footer = styled.footer`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  margin-top: 20px;
`;

  // Define Logo styled component
  const Logo = styled.img`
    width: 100px; /* Adjust size as needed */
    height: auto; /* Maintain aspect ratio */
  `;



  const renderContent = () => {
    return (
      <>
     <Header>Random Color Generator and Palette Finder</Header>
              <ContentContainer>
                <p>Implementing a 6:3:1 ratio for your brand's color scheme ensures a harmonious and unified design approach, fostering precision and consistency across all platforms. This method enriches your brand's visual identity, effortlessly enhancing its aesthetic appeal in any setting and context.</p>
                
              </ContentContainer>
              <PiMouseScrollBold />
        <InputContainer>
        <SubHeader>Generate a Color Palette</SubHeader>
        <Line />
      
          <Input
            type="text"
            value={inputColor}
            onChange={(e) => setInputColor(e.target.value)}
            placeholder="Enter HEX"
          />
            
          <Button onClick={handleGeneratePalette}>Generate Palette</Button>
          <Button onClick={handleGenerateRandomColor}>Random Color</Button>
        </InputContainer>
        <CustomChromePicker color={inputColor} onChange={handleColorChange} />
        <DropzoneContainer {...getRootProps()}>
          <input {...getInputProps()} />
          {uploadedImage ? (
            <img src={uploadedImage} alt="Uploaded" style={{ maxWidth: '100%', maxHeight: '200px' }} />
          ) : (
            <p>Drag 'n' drop an image here, or click to select image</p>
          )}
        </DropzoneContainer>
        <ColorDisplay>
          {colorPalette.map((color, index) => (
            <ColorBoxContainer key={index}>
              <ColorBox
                color={color}
                onClick={() => handleColorBoxClick(index, color)}
                className={selectedColorIndex === index ? 'selected' : ''}
              >
                <ColorCode>{color}</ColorCode>
              </ColorBox>
            </ColorBoxContainer>
          ))}
        </ColorDisplay>
        <SampleBox
          textColor={sampleTextColor}
          bgColor={sampleBgColor}
          borderColor={sampleBorderColor}
        >
          {displayText}
        </SampleBox>
        <AdditionalColorsContainer>
          {additionalColors.map((color, index) => (
            <AdditionalColorBox
              key={index}
              color={color}
              onClick={() => setInputColor(color)}
              className={inputColor === color ? 'selected' : ''}
            />
          ))}
        </AdditionalColorsContainer>
        <Button onClick={handleGeneratePalette}>Generate Palette</Button>
        <Button onClick={handleGenerateRandomColor}>Random Color</Button>

        
      </>
    );
  };

  return (
    <BrowserRouter>
      <AppContainer>
        
        <NavBar>
          <NavLinkStyled to="/">Home</NavLinkStyled>
          <NavLinkStyled to="/about">About</NavLinkStyled>
          <NavLinkStyled to="/contact">Contact</NavLinkStyled>
          <NavLinkStyled to="/privacy-policy">PrivacyPolicy</NavLinkStyled>
          <NavLinkStyled to="/why-colors-matter">Why Colors Matter</NavLinkStyled> {/* Add this line */}
        </NavBar>
        
        
        <Routes>
          <Route path="/" element={renderContent()} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/why-colors-matter" element={<WhyColorsMatter />} />
        </Routes>
        
      
        
        <Footer>
        <Line />
          <p>This is created by LA LAB</p>
          <Logo src={logoImage} alt="LA LAB Logo" />
        </Footer>
      </AppContainer>
    </BrowserRouter>
  );
};

export default App;